/* eslint-disable indent */
/* eslint-disable @typescript-eslint/indent */
import type React from 'react';
import type { ComponentProps } from 'react';
import type { CacheHookArgs } from '../../../lib/cache/hooks/useCache';
import PaginateComponent from '../Pagination/PaginateComponent';
import TableBody from './TableBody/TableBody';
import TableCell from './TableCell/TableCell';
import TableHeader from './TableHeader/TableHeader';
import TableHeaderCell from './TableHeaderCell/TableHeaderCell';
import TableRow from './TableRow/TableRow';

type TableHeaderCellProps = ComponentProps<typeof TableHeaderCell>;
interface TableHeading extends Omit<TableHeaderCellProps, 'children'> {
    key: string;
    label: string;
}

interface Props<T> {
    headings: TableHeading[];
    onTableHeadingClick?: (headingKey: string) => void;

    searchTerm?: string; // This is purely used for passing to the Pagination Component to reset the page on search change.
    data: T[];
    children: (row: T) => React.JSX.Element | React.JSX.Element[];

    pagination?: boolean;
    perPage?: number;

    minWidth?: string;
    cache?: CacheHookArgs['cache'];
    cacheKey?: CacheHookArgs['cacheKey'];
}

const Table = <T extends GenericObject>({
    headings,
    onTableHeadingClick,

    searchTerm,
    data,
    children,

    pagination,
    perPage = 10,

    minWidth,
    cache,
    cacheKey,
}: Props<T>) => (
    <PaginateComponent
        pagination={pagination}
        perPage={perPage}
        data={data}
        cache={cache}
        cacheKey={cacheKey}
        searchTerm={searchTerm}
    >
        {(dataToDisplay) => (
            <div className="overflow-x-auto overflow-y-hidden relative z-[1]" data-table-container="js-table-container">
                <table className="w-full" style={{ minWidth }} data-table-wrapper="js-table-wrapper">
                    <TableHeader>
                        <TableRow key="table_header_row" hideBorder>
                            {headings &&
                                headings.map(({ key, label, ...headingProps }) => (
                                    <TableHeaderCell
                                        key={key}
                                        {...headingProps}
                                        onClick={onTableHeadingClick ? () => onTableHeadingClick(key) : undefined}
                                    >
                                        {label}
                                    </TableHeaderCell>
                                ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>{dataToDisplay && dataToDisplay.map((row) => children(row))}</TableBody>
                </table>
            </div>
        )}
    </PaginateComponent>
);

Table.Cell = TableCell;
Table.Row = TableRow;

export default Table;
